import { movies, type MockMovie } from "@/mocks/movies";

export type SortKey = "updated" | "hot" | "rating" | "year";

export interface MovieFilter {
  type?: string;
  region?: string;
  year?: string;
  genre?: string;
}

export function getMovieById(id: string | undefined): MockMovie | undefined {
  if (!id) return undefined;
  return movies.find((m) => m.id === id);
}

function matchYear(movie: MockMovie, year: string): boolean {
  if (!year || year === "全部") return true;
  if (year === "更早") return movie.year < 2022;
  return movie.year === Number(year);
}

export function filterMovies(filter: MovieFilter): MockMovie[] {
  const { type, region, year, genre } = filter;
  return movies.filter((m) => {
    if (type && type !== "全部" && m.type !== type) return false;
    if (region && region !== "全部" && m.region !== region) return false;
    if (genre && genre !== "全部" && !m.genres.includes(genre)) return false;
    if (!matchYear(m, year || "全部")) return false;
    return true;
  });
}

export function sortMovies(list: MockMovie[], sort: SortKey): MockMovie[] {
  const copy = [...list];
  switch (sort) {
    case "hot":
      return copy.sort((a, b) => b.views - a.views);
    case "rating":
      return copy.sort((a, b) => b.rating - a.rating);
    case "year":
      return copy.sort((a, b) => b.year - a.year);
    case "updated":
    default:
      return copy.sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
  }
}

export function searchMovies(keyword: string): MockMovie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies.filter((m) => {
    return (
      m.title.toLowerCase().includes(q) ||
      m.director.toLowerCase().includes(q) ||
      m.genres.some((g) => g.toLowerCase().includes(q)) ||
      m.region.toLowerCase().includes(q) ||
      m.cast.some((c) => c.toLowerCase().includes(q)) ||
      m.tags.some((t) => t.toLowerCase().includes(q))
    );
  });
}

export function getRelatedMovies(movie: MockMovie, count = 6): MockMovie[] {
  const scored = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      let score = 0;
      score += m.genres.filter((g) => movie.genres.includes(g)).length * 3;
      score += m.tags.filter((t) => movie.tags.includes(t)).length * 2;
      if (m.region === movie.region) score += 1;
      if (m.type === movie.type) score += 1;
      return { movie: m, score };
    })
    .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating);
  return scored.slice(0, count).map((s) => s.movie);
}

export function getLatestMovies(count = 12): MockMovie[] {
  return sortMovies(movies, "updated").slice(0, count);
}

export function getHotMovies(count = 8): MockMovie[] {
  return sortMovies(movies, "hot").slice(0, count);
}